// Szekció egy form-/detail-oldal KÖZÖS, görgethető felületén belül — NEM Card, NINCS keret, NINCS külön
// háttér. Csak cím (+ opcionális leírás, jobb-oldali akció) és a tartalom, egységes térközzel. A vizuális
// tagolást a szekció-cím és a szekciók közti térköz adja (az oldal `space-y-8`-a), nem keret/hairline.
//
// A szekció-cím tipográfiája az `overline` variant (11 / 600 / tertiary + CAPS + tracking) — ugyanaz, mint a
// sidebar csoport-nevei és a DataTable oszlop-fejlécei (design.md §2), így az egész felület egy rendszer.
//
// EREDET: a profil-oldalon született (`ProfileSection`), és a form-oldalak is ezt a mintát követik → a repo
// DRY-szabálya szerint (CLAUDE.md 4. főszabály: „ha 2. helyen másolódna, a közös rétegbe kell tenni") a
// `components/shared/` alá került. A profil a `ProfileSection` néven ezt re-exportálja.
import type { ReactNode } from 'react';

import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';

export function FormSection({
  id,
  title,
  description,
  action,
  children,
}: {
  /**
   * Horgony a ragadós szekció-navigációhoz (`SectionNav`). Ha meg van adva, a szekció ugrás-célponttá válik.
   * A `scroll-mt-6` nélkül a cím a görgetés-port LEGTETEJÉRE tapadna — a `scroll-margin` ad neki levegőt.
   * A `tabIndex={-1}`: az ugrás után a fókusz IDE kerül, így a billentyűzetes olvasás innen folytatódik
   * (különben a rail elejére ugrana vissza) — de a szekció nem kerül be a Tab-sorrendbe.
   */
  id?: string | undefined;
  title: string;
  /** `| undefined` explicit — a repo `exactOptionalPropertyTypes: true`-val fut. */
  description?: string | undefined;
  action?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <section
      {...(id ? { id, tabIndex: -1 } : {})}
      className="scroll-mt-6 space-y-4 focus:outline-none"
    >
      {/* A jobb-oldali akció függőleges igazítása a fejléc ALAKJÁTÓL függ (user-észrevétel, 2026-08-18):
          • leírás NÉLKÜL a fejléc egyetlen sor → `items-center`, így a szekció-cím a gomb FÜGGŐLEGES KÖZEPÉVEL
            van egy vonalban. `items-start` mellett a 11px-es overline a ~32px magas gomb tetejéhez tapadt, és
            optikailag megcsúszott.
          • leírás ESETÉN a bal oldal két soros → a középre igazítás az akciót a cím ALÁ, a két sor közé
            húzná. Ilyenkor a cím első sorához kell igazodni (`items-start`) — a profil 2FA-kártyája ilyen
            (cím + leírás + státusz-badge). */}
      <div
        className={cn('flex justify-between gap-3', description ? 'items-start' : 'items-center')}
      >
        <div className="space-y-1">
          <Text variant="overline" as="h2">
            {title}
          </Text>
          {description && <p className="text-muted-foreground text-sm">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}
